import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { CreateAddressInput } from "@/api/routes/addresses";
import { useApi } from "../../hooks/api/use-api";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";
import { Textarea } from "../ui/textarea";
import GermanPhoneInput from "./GermanPhoneInput";

interface AddressFormProps {
	formData: CreateAddressInput;
	onChange: (field: keyof CreateAddressInput, value: string | boolean) => void;
	errors: Partial<CreateAddressInput>;
	mode?: "create" | "edit";
	phoneRequired?: boolean;
}

export default function AddressForm({
	formData,
	onChange,
	errors,
	mode = "create",
	phoneRequired = false,
}: AddressFormProps) {
	const { t } = useTranslation();
	const { data: zonesData } = useApi().users.addresses.getZones();
	const zones = zonesData?.zones ?? [];

	// Get postal codes for selected zone (formData.city holds zone name when using API)
	const getPostalCodesForZone = (zoneName: string) => {
		const zone = zones.find((z) => z.name === zoneName);
		return zone?.postalCodes ?? [];
	};

	// Get zone info for selected postal code
	const getZoneInfo = (postalCode: string) => {
		const zone = zones.find((z) => z.postalCodes.includes(postalCode));
		if (!zone) return null;
		return {
			name: zone.name,
			minOrder: parseFloat(zone.minimumOrder ?? "0"),
			deliveryFee: parseFloat(zone.deliveryFee ?? "0"),
		};
	};

	// Handle zone (city) selection - auto-select first available postal code
	const handleZoneChange = (zoneName: string) => {
		onChange("city", zoneName);
		onChange("state", "Nordrhein-Westfalen"); // We can keep this value hardcoded as logic, but display using key if printed.

		const postalCodes = getPostalCodesForZone(zoneName);
		if (postalCodes.length > 0 && !formData.zipCode) {
			onChange("zipCode", postalCodes[0]);
		}
	};

	// Handle postal code selection - show zone info
	const handlePostalCodeChange = (postalCode: string) => {
		onChange("zipCode", postalCode);
		const zone = getZoneInfo(postalCode);
		if (zone) {
			toast.success(
				t("address_dialog.toast.zone_selected", {
					name: zone.name,
					minOrder: zone.minOrder.toFixed(2),
				}),
			);
		}
	};

	const selectedZone = formData.zipCode ? getZoneInfo(formData.zipCode) : null;
	const zoneNames = zones.map((z) => z.name);

	return (
		<div className="space-y-6">
			{/* Address Label */}
			<div className="space-y-2">
				<Label htmlFor="label" className="text-sm font-medium">
					{t("address_dialog.label_label")}
				</Label>
				<Input
					id="label"
					value={formData.label}
					onChange={(e) => onChange("label", e.target.value)}
					placeholder={t("address_dialog.label_placeholder")}
					className="h-11"
				/>
				{errors.label && (
					<p className="text-sm text-destructive mt-1">{errors.label}</p>
				)}
			</div>

			{/* Country - Fixed */}
			<div className="space-y-2">
				<Label className="text-sm font-medium">
					{t("address_dialog.country_label")}
				</Label>
				<div className="h-11 px-3 py-2 bg-muted rounded-md border flex items-center">
					<span className="text-sm">
						🇩🇪 {t("address_dialog.country_germany")}
					</span>
				</div>
				<p className="text-xs text-muted-foreground">
					{t("address_dialog.country_note")}
				</p>
			</div>

			{/* Delivery Zone (saved as city for API) */}
			<div className="space-y-2">
				<Label htmlFor="city" className="text-sm font-medium">
					{t("address_dialog.zone_label")}
				</Label>
				<Select
					value={formData.city}
					onValueChange={handleZoneChange}
					disabled={zoneNames.length === 0}
				>
					<SelectTrigger className="h-11">
						<SelectValue
							placeholder={
								zoneNames.length === 0
									? t("address_dialog.zone_loading")
									: t("address_dialog.zone_placeholder")
							}
						/>
					</SelectTrigger>
					<SelectContent>
						{zoneNames.map((name) => (
							<SelectItem key={name} value={name}>
								{name}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
				{errors.city && (
					<p className="text-sm text-destructive mt-1">{errors.city}</p>
				)}
			</div>

			{/* Postal Code Selector - filtered by zone */}
			<div className="space-y-2">
				<Label htmlFor="zipCode" className="text-sm font-medium">
					{t("address_dialog.postal_code_label")}
				</Label>
				<Select
					value={formData.zipCode}
					onValueChange={handlePostalCodeChange}
					disabled={!formData.city}
				>
					<SelectTrigger className="h-11">
						<SelectValue
							placeholder={
								formData.city
									? t("address_dialog.postal_code_placeholder")
									: t("address_dialog.select_zone_first")
							}
						/>
					</SelectTrigger>
					<SelectContent>
						{formData.city &&
							getPostalCodesForZone(formData.city).map((postalCode) => {
								const zone = getZoneInfo(postalCode);
								return (
									<SelectItem key={postalCode} value={postalCode}>
										<div className="flex gap-2 items-center">
											<span>{postalCode}</span>
											{zone && (
												<span className="text-xs text-muted-foreground">
													{zone.name} • €{zone.minOrder.toFixed(2)} min
												</span>
											)}
										</div>
									</SelectItem>
								);
							})}
					</SelectContent>
				</Select>
				{selectedZone && (
					<div className="p-3 bg-primary/5 rounded-lg border border-primary/20">
						<div className="flex items-center gap-2 text-sm">
							<span className="font-medium text-primary">
								{selectedZone.name}
							</span>
						</div>
						<div className="flex gap-4 mt-1 text-xs text-muted-foreground">
							<span>
								{t("address_dialog.min_order")}: €
								{selectedZone.minOrder.toFixed(2)}
							</span>
							<span>
								{t("address_dialog.delivery_fee")}:{" "}
								{selectedZone.deliveryFee === 0
									? t("address_dialog.delivery_free")
									: `€${selectedZone.deliveryFee.toFixed(2)}`}
							</span>
						</div>
					</div>
				)}
				{errors.zipCode && (
					<p className="text-sm text-destructive mt-1">{errors.zipCode}</p>
				)}
			</div>

			{/* Street Address */}
			<div className="space-y-2">
				<Label htmlFor="streetAddress" className="text-sm font-medium">
					{t("address_dialog.street_label")}
				</Label>
				<Input
					id="streetAddress"
					value={formData.streetAddress}
					onChange={(e) => onChange("streetAddress", e.target.value)}
					placeholder={t("address_dialog.street_placeholder")}
					className="h-11"
				/>
				{errors.streetAddress && (
					<p className="text-sm text-destructive mt-1">
						{errors.streetAddress}
					</p>
				)}
			</div>

			{/* State - Fixed for Ruhr area */}
			<div className="space-y-2">
				<Label className="text-sm font-medium">
					{t("address_dialog.state_label")}
				</Label>
				<div className="h-11 px-3 py-2 bg-muted rounded-md border flex items-center">
					<span className="text-sm">{t("address_dialog.state_value")}</span>
				</div>
			</div>

			{/* Phone Number */}
			<GermanPhoneInput
				id="phone"
				value={formData.phone}
				onChange={(value) => onChange("phone", value)}
				error={errors.phone}
				required={phoneRequired}
			/>

			{/* Delivery Instructions */}
			<div className="space-y-2">
				<Label htmlFor="instructions" className="text-sm font-medium">
					{t("address_dialog.instructions_label")}{" "}
					<span className="text-muted-foreground font-normal">
						{t("address_dialog.instructions_optional")}
					</span>
				</Label>
				<Textarea
					id="instructions"
					value={formData.instructions}
					onChange={(e) => onChange("instructions", e.target.value)}
					placeholder={t("address_dialog.instructions_placeholder")}
					className="min-h-[80px] resize-none"
				/>
				{errors.instructions && (
					<p className="text-sm text-destructive mt-1">{errors.instructions}</p>
				)}
			</div>

			{/* Set as Default */}
			{mode === "create" && (
				<div
					className="flex items-center space-x-3 p-4 bg-secondary/20 rounded-lg cursor-pointer hover:bg-secondary/30 transition-colors"
					onClick={() => onChange("isDefault", !formData.isDefault)}
				>
					<input
						id="isDefault"
						type="checkbox"
						checked={formData.isDefault}
						onChange={() => onChange("isDefault", !formData.isDefault)}
						className="pointer-events-none"
					/>
					<div className="space-y-1">
						<Label htmlFor="isDefault" className="text-sm font-medium">
							{t("address_dialog.default_label")}
						</Label>
						<p className="text-xs text-muted-foreground">
							{t("address_dialog.default_description")}
						</p>
					</div>
				</div>
			)}
		</div>
	);
}
